import React, { useState, useEffect } from 'react';
import { ExternalLink, Handshake, Loader2, ImageIcon } from 'lucide-react';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPartners();
  }, []);

  const loadPartners = async () => {
    try {
      const res = await axios.get(`${API}/partners`);
      setPartners(res.data.partners || []);
    } catch (e) {
      console.error('Error loading partners:', e);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex flex-col">
      <NavigationMenu />
      <main className="flex-1 pt-28 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-300 text-sm mb-4">
              <Handshake className="w-4 h-4" /> Nos partenaires
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="partners-page-title">
              Nos partenaires
            </h1>
            <p className="text-blue-300/60 text-lg max-w-2xl mx-auto">
              Découvrez les entreprises et organisations qui nous font confiance et collaborent avec SENDBOX.
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-20" data-testid="no-partners-public">
              <Handshake className="w-16 h-16 text-blue-300/20 mx-auto mb-4" />
              <p className="text-blue-300/40 text-lg">Aucun partenaire pour le moment</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
              {partners.map(p => (
                <a
                  key={p.partner_id}
                  href={p.website_url || '#'}
                  target={p.website_url ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="group bg-slate-800/40 border border-blue-500/10 rounded-xl p-6 hover:border-teal-500/30 hover:bg-slate-800/60 transition-all duration-300"
                  data-testid={`public-partner-${p.partner_id}`}
                >
                  <div className="w-20 h-20 rounded-xl bg-white/5 border border-blue-500/10 flex items-center justify-center mx-auto mb-4 overflow-hidden group-hover:border-teal-500/20 transition-colors">
                    {p.logo_url ? (
                      <img
                        src={p.logo_url}
                        alt={p.name}
                        className="w-full h-full object-contain p-2"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-blue-300/20" />
                    )}
                  </div>
                  <h3 className="text-white font-semibold text-center text-lg mb-2 group-hover:text-teal-300 transition-colors">
                    {p.name}
                  </h3>
                  {p.description && (
                    <p className="text-blue-300/50 text-sm text-center line-clamp-3">{p.description}</p>
                  )}
                  {p.website_url && (
                    <div className="mt-3 flex items-center justify-center gap-1 text-teal-400/60 text-xs group-hover:text-teal-400 transition-colors">
                      <ExternalLink className="w-3 h-3" /> Visiter le site
                    </div>
                  )}
                </a>
              ))}
            </div>
          )}
        </div>
      </main>
      <DynamicFooter />
    </div>
  );
};

export default PartnersPage;
